import { useState } from "react";
import { Sparkles, Database, Info } from "lucide-react";
import { CustomBadge } from "@/components/ui/custom-badge";

interface Suggestion {
  modality: string;
  rationale: string;
}

interface MatchedTherapy {
  name_en: string;
  name_cn: string;
  type: string;
  evidence_level: string | null;
  mechanism_en: string | null;
  mechanism_cn: string | null;
  match_score: number;
}

interface Props {
  suggestions: Suggestion[];
  matchedTherapies?: MatchedTherapy[];
}

const EVIDENCE_BADGE_VARIANT: Record<string, "default" | "secondary" | "success" | "outline"> = {
  "Strong": "success",
  "Moderate": "default",
  "Emerging": "secondary",
  "Traditional": "outline",
};

const EVIDENCE_INFO: Record<string, { en: string; zh: string }> = {
  A: {
    en: "Strong Evidence — Multiple systematic reviews/meta-analyses + large-scale RCTs. High consensus, insurance-reimbursable. (e.g. CBT, DBT, IPT)",
    zh: "强证据 — 多个系统综述/荟萃分析 + 大量随机对照试验验证。共识度高，多纳入医保。(如 CBT、DBT、IPT)",
  },
  B: {
    en: "Moderate Evidence — 1-2 quality RCTs or substantial cohort/case-control studies. Strong direction but limited replication. (e.g. EFT, ACT, some CBT variants)",
    zh: "中等证据 — 1-2 个高质量 RCT 或较多队列/病例对照研究。方向明确但重复性不足。(如 EFT、ACT、部分 CBT 变体)",
  },
  C: {
    en: "Limited Evidence — Small non-randomized studies, open trials, or clinical case series. Shows promise but lacks large-scale validation. (e.g. art therapy, some somatic approaches)",
    zh: "有限证据 — 少量非随机化研究、开放性试验或临床案例报告。有一定效果但缺乏大规模验证。(如艺术疗法、部分躯体疗法)",
  },
  D: {
    en: "Emerging Evidence — Primarily anecdotal, traditional practice, or early-stage research. Not yet validated but not disproven. (e.g. sound healing, breathwork, plant medicine)",
    zh: "新兴证据 — 主要基于个案经验、传统实践或早期研究。尚未被系统验证但也未被否定。(如声音疗愈、呼吸法、植物药)",
  },
};

function getEvidenceKey(level: string): string | null {
  if (level.includes("Strong")) return "A";
  if (level.includes("Moderate")) return "B";
  if (level.includes("Limited") || level.includes("Emerging")) return level.includes("Limited") ? "C" : "D";
  if (level === "A" || level === "B" || level === "C" || level === "D") return level;
  return null;
}

export default function TherapySuggestions({ suggestions, matchedTherapies }: Props) {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  return (
    <div className="space-y-4 md:space-y-5">
      {/* AI Suggestions */}
      <div className="space-y-2 md:space-y-3">
        <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
          <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-primary" />
          AI Therapy Pathway Suggestions
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground">
          AI-generated recommendations based on your consciousness profile
        </p>
        <div className="grid gap-2 md:gap-3 grid-cols-1 sm:grid-cols-2">
          {suggestions.map((s, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-3 md:p-4 hover:shadow-sm transition-shadow"
            >
              <h4 className="font-semibold text-foreground text-sm md:text-base mb-1">{s.modality}</h4>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{s.rationale}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Matched DB Therapies */}
      {matchedTherapies && matchedTherapies.length > 0 && (
        <div className="space-y-2 md:space-y-3">
          <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
            <Database className="h-4 w-4 md:h-5 md:w-5 text-primary" />
            Matched Therapies from Database
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Precision-matched from our therapy modalities database
          </p>
          <div className="grid gap-2 md:gap-3 grid-cols-1 sm:grid-cols-2">
            {matchedTherapies.map((t, i) => {
              const evidenceKey = t.evidence_level ? getEvidenceKey(t.evidence_level) : null;
              const tooltipId = `evidence-${i}`;
              const isOpen = openTooltip === tooltipId;

              return (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-card p-3 md:p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="font-semibold text-foreground text-sm md:text-base">{t.name_en}</h4>
                    {t.evidence_level && (
                      <div className="flex items-center gap-1 shrink-0">
                        <CustomBadge
                          variant={EVIDENCE_BADGE_VARIANT[t.evidence_level] || "outline"}
                          className="text-[10px]"
                        >
                          {t.evidence_level}
                        </CustomBadge>
                        {evidenceKey && EVIDENCE_INFO[evidenceKey] && (
                          <button
                            type="button"
                            className="relative"
                            onClick={() => setOpenTooltip(isOpen ? null : tooltipId)}
                            aria-label={`Evidence level info`}
                          >
                            <Info className="h-3 w-3 md:h-3.5 md:w-3.5 text-muted-foreground" />
                            {isOpen && (
                              <div className="absolute bottom-full right-0 mb-1.5 px-3 py-2 bg-foreground text-background text-[10px] md:text-xs rounded-md w-[280px] md:w-[340px] z-20 leading-relaxed shadow-lg text-left">
                                <p>{EVIDENCE_INFO[evidenceKey].en}</p>
                              </div>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {t.name_cn && (
                    <p className="text-[10px] md:text-xs text-muted-foreground mb-1">{t.name_cn}</p>
                  )}
                  <p className="text-[10px] md:text-xs text-muted-foreground/70 mb-1.5 md:mb-2">{t.type}</p>
                  {t.mechanism_en && (
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{t.mechanism_en}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
